/**
 * HISIP-013: ProfileSummary Component
 * 
 * Displays selected Creative Profile with preset and stage configurations
 * Shows project_id scoped execution context (not brand theming)
 */

import React from 'react';
import { motion } from 'framer-motion';
import { User, Settings, Zap, Cpu, Palette, Target, ExternalLink } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface ProfileConfig {
  name: string;
  preset?: string;
  stages: Record<string, StageConfig>;
}

export interface StageConfig {
  persona_name?: string;
  persona_slug?: string;
  persona_version?: string;
  rubric_variant?: string;
  rubric_slug?: string;
  rubric_version?: string;
  template_slug?: string;
  template_version?: string;
  provider?: string;
  model?: string;
  model_slug?: string;
  model_version?: string;
  default_style?: string;
  target_threshold?: number;
  fallback_threshold?: number;
}

interface ProfileSummaryProps {
  profile?: ProfileConfig;
  projectId?: string;
  className?: string;
  compact?: boolean;
  onManageProfiles?: () => void;
}

const ProfileSummary: React.FC<ProfileSummaryProps> = ({
  profile,
  projectId,
  className,
  compact = false,
  onManageProfiles
}) => {
  if (!profile) {
    return (
      <div className={cn(
        "bg-black/30 rounded-lg border border-white/10 p-4",
        className
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-400">
            <User className="w-4 h-4" />
            <span className="text-sm">No profile selected</span>
          </div>
          {onManageProfiles && (
            <button
              onClick={onManageProfiles}
              className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded hover:bg-purple-500/30 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Manage Profiles
            </button>
          )}
        </div>
      </div>
    );
  }

  const stageEntries = Object.entries(profile.stages);
  const stageCount = stageEntries.length;

  if (compact) {
    return (
      <motion.div
        className={cn(
          "bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-lg border border-purple-500/20 p-3",
          className
        )}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
              <User className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <div className="text-sm font-medium text-white">
                {profile.name}
              </div>
              {profile.preset && (
                <div className="text-xs text-purple-300">
                  {profile.preset}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-white/60">
              <Settings className="w-3 h-3" />
              {stageCount} stages
            </div>
            {onManageProfiles && (
              <button
                onClick={onManageProfiles}
                className="flex items-center gap-1 px-2 py-1 bg-purple-500/10 text-purple-300 text-xs rounded hover:bg-purple-500/20 transition-colors"
                title="Manage AI Settings"
              >
                <ExternalLink className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        </div>
        
        {projectId && (
          <div className="mt-2 text-xs text-cyan-300 font-mono">
            Project: {projectId}
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={cn(
        "bg-black/30 rounded-lg border border-white/10 p-4",
        className
      )}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Profile Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/30 to-cyan-500/30 border border-purple-500/40 flex items-center justify-center">
            <User className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-white">
              {profile.name}
            </h3>
            {profile.preset && (
              <div className="flex items-center gap-1 text-sm text-purple-300">
                <Zap className="w-3 h-3" />
                {profile.preset}
              </div>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-sm text-white/60">
            {stageCount} Stages Configured
          </div>
          {projectId && (
            <div className="text-xs text-cyan-300 font-mono mt-1">
              {projectId}
            </div>
          )}
        </div>
      </div>

      {/* Stage Configurations */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-white/80 uppercase tracking-wider">
          Stage Configuration
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {stageEntries.map(([stageName, config]) => (
            <motion.div
              key={stageName}
              className="bg-black/20 rounded-lg border border-white/5 p-3"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: stageEntries.indexOf([stageName, config]) * 0.05 }}
            >
              {/* Stage Name */}
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                <span className="text-sm font-medium text-white capitalize">
                  {stageName}
                </span>
              </div>

              {/* Configuration Details */}
              <div className="space-y-1.5 text-xs">
                {config.persona_name && (
                  <div className="flex items-center gap-2">
                    <User className="w-3 h-3 text-purple-400" />
                    <span className="text-purple-300">
                      {config.persona_name}
                      {config.persona_slug && config.persona_version && (
                        <span className="text-purple-400/60 text-xs ml-1 font-mono">
                          {config.persona_slug}@{config.persona_version}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                
                {config.provider && config.model && (
                  <div className="flex items-center gap-2">
                    <Cpu className="w-3 h-3 text-cyan-400" />
                    <span className="text-cyan-300">
                      {config.provider}/{config.model}
                      {config.model_slug && config.model_version && (
                        <span className="text-cyan-400/60 text-xs ml-1 font-mono">
                          {config.model_slug}@{config.model_version}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                
                {config.rubric_variant && (
                  <div className="flex items-center gap-2">
                    <Target className="w-3 h-3 text-emerald-400" />
                    <span className="text-emerald-300">
                      {config.rubric_variant}
                      {config.rubric_slug && config.rubric_version && (
                        <span className="text-emerald-400/60 text-xs ml-1 font-mono">
                          {config.rubric_slug}@{config.rubric_version}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                
                {config.default_style && (
                  <div className="flex items-center gap-2">
                    <Palette className="w-3 h-3 text-amber-400" />
                    <span className="text-amber-300">{config.default_style}</span>
                  </div>
                )}
                
                {config.target_threshold && (
                  <div className="flex items-center gap-2 justify-between">
                    <div className="flex items-center gap-1">
                      <Target className="w-3 h-3 text-yellow-400" />
                      <span className="text-yellow-300">Quality</span>
                    </div>
                    <div className="text-yellow-300 font-mono">
                      {config.target_threshold}%/{config.fallback_threshold || 70}%
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-4 pt-3 border-t border-white/10">
        <div className="flex items-center justify-between text-xs text-white/60">
          <div className="flex items-center gap-4">
            <span>
              {Object.values(profile.stages).filter(s => s.persona_name).length} Personas
            </span>
            <span>
              {new Set(Object.values(profile.stages).map(s => s.provider).filter(Boolean)).size} Providers
            </span>
            <span>
              {Object.values(profile.stages).filter(s => s.rubric_variant).length} Rubrics
            </span>
          </div>
          
          <div className="font-mono">
            Profile Ready
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProfileSummary;